import React from 'react';
import { Signal } from '@demind-inc/core';
import './richContext.scss';

interface WeatherTimeslot {
  timestamp: string;
  temperature_f?: number;
  temperature_c?: number;
  summary?: string;
}

function weatherIcon(summary: string | undefined): string {
  if (!summary) return '🌤';
  const s = summary.toLowerCase();
  if (s.includes('thunder') || s.includes('storm')) return '⛈';
  if (s.includes('snow') || s.includes('blizzard')) return '🌨';
  if (s.includes('sleet') || s.includes('freezing')) return '🌧';
  if (s.includes('rain') || s.includes('shower') || s.includes('drizzle')) return '🌧';
  if (s.includes('fog') || s.includes('mist') || s.includes('haze')) return '🌫';
  if (s.includes('wind') || s.includes('breezy') || s.includes('gusty')) return '💨';
  if (s.includes('overcast') || s.includes('cloudy')) return '☁️';
  if (s.includes('partly') || s.includes('mostly cloudy')) return '⛅';
  if (s.includes('clear') || s.includes('sunny') || s.includes('fair')) return '☀️';
  return '🌤';
}

function formatHour(timestamp: string): string {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const h = date.getHours();
  if (h === 0) return '12am';
  if (h < 12) return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}

interface WeatherSignalCardProps {
  signal: Signal;
}

const WeatherSignalCard: React.FC<WeatherSignalCardProps> = ({ signal }) => {
  const data = signal.data as Record<string, unknown> | null;
  const timeline = Array.isArray(data?.timeline) ? (data!.timeline as WeatherTimeslot[]) : [];
  const hasTimeline = timeline.length > 0;

  return (
    <div className="rc-weather-card">
      <div className="rc-weather-header">
        <span className="rc-signal-icon">🌤</span>
        <div className="rc-weather-header-text">
          <div className="rc-signal-source">weather</div>
          {!!signal.body && <div className="rc-signal-body">{signal.body}</div>}
        </div>
      </div>

      {hasTimeline && (
        <div className="rc-weather-timeline-scroll">
          {timeline.map((slot, i) => {
            const hour = formatHour(slot.timestamp);
            const icon = weatherIcon(slot.summary);
            const temp =
              typeof slot.temperature_c === 'number'
                ? `${Math.round(slot.temperature_c)}°`
                : typeof slot.temperature_f === 'number'
                ? `${Math.round(slot.temperature_f)}°F`
                : null;

            return (
              <div key={i} className="rc-weather-slot">
                {!!hour && <span className="rc-weather-slot-time">{hour}</span>}
                <span className="rc-weather-slot-icon">{icon}</span>
                {temp !== null && <span className="rc-weather-slot-temp">{temp}</span>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WeatherSignalCard;
